import { useLayoutEffect, useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
    CaseApi,
    CaseGroupApi,
    CaseTypeApi,
    CustomerApi,
    CustomerLinkApi,
    PriorityApi,
    ResolutionStatusApi,
} from "../../../../api";
import useAuth from "../../../../Hooks/useAuth";
import { IResolutionStatusRes, IPriorityRes, IClassReportRes, ICaseGroupRes, ICaseTypeRes, IEmployeeBasicRes, IContactBasicRes, ILinkRes, ISelTypeRes, IEmployeeBasicReportRes } from "./types";
import useAxiosPrivate from "../../../../Hooks/useAxiosPrivate";
import { formatDateWithTimeZone } from "../../../../_helper/dateHelper";
import { ContactTypeVal, createDropdownField } from "../../../../Data/FormField/PublicData";
import { Contact, Link } from "../../../../Constant";

export default function useCaseReport() {
    const axiosPrivate = useAxiosPrivate();
    const { userOrganisationId }: any = useAuth();

    const [dateRange, setDateRange] = useState<Date[] | null[]>([null, null]);
    const [startDate, endDate] = dateRange;

    const [loading, setLoading] = useState(false);

    const [PriorityDropDown, setPriorityDropDown] = useState<IPriorityRes[]>([]);
    const [PriorityDropDownValue, setPriorityDropDownValue] = useState("0");

    const [ResolutionStatusDropDown, setResolutionStatusDropDown] = useState<IResolutionStatusRes[]>([]);
    const [ResolutionStatusDropDownValue, setResolutionStatusDropDownValue] = useState("0");
    
    const [CustomerDropDownValue, setCustomerDropDownValue] = useState("0");
    
    const linkDropdownField = createDropdownField(axiosPrivate,
        'link_id',
        'link_id',
        `${Link}`,
        ``,
        '', 6, true, 
        "contact_branch.contact_number, contact_branch.name | contact.contact_number, contact.company_name | contact.contact_number, contact.last_name, contact.first_name | [product.name]"
    );
    
    const [LinkDropDown, setLinkDropDown] = useState<ISelTypeRes[]>([]);
    const [LinkDropDownValue, setLinkDropDownValue] = useState("0");

    const [CaseGroupDropDown, setCaseGroupDropDown] = useState<ICaseGroupRes[]>([]);
    const [CaseGroupDropDownValue, setCaseGroupDropDownValue] = useState("0");

    const [CaseTypeDropDown, setCaseTypeDropDown] = useState<ICaseTypeRes[]>([]);
    const [CaseTypeDropDownValue, setCaseTypeDropDownValue] = useState("0");
    
    const [TrackingNumberValue, setTrackingNumberValue] = useState("");

    const [printedData, setPrintedData] = useState<IClassReportRes[]>([]);

    async function getPriority() {
        try {
            axiosPrivate
                .get(
                    `${PriorityApi}?&organisation_id=${userOrganisationId}`
                )
                .then((res) => {
                    setPriorityDropDown(res.data);
                });
        } catch (err: any) {
            if (err.response && err.response.status === 404) {
                toast.error(err.response.data.detail);
            } else {
                toast.error("Whoops! Something went wrong");
            }
        }
    }

    async function getResolutionStatus() {
        try {
            axiosPrivate
                .get(
                    `${ResolutionStatusApi}?&organisation_id=${userOrganisationId}`
                )
                .then((res) => {
                    setResolutionStatusDropDown(res.data);
                });
        } catch (err: any) {
            if (err.response && err.response.status === 404) {
                toast.error(err.response.data.detail);
            } else {
                toast.error("Whoops! Something went wrong");
            }
        }
    }

    async function getCaseGroup() {
        try {
            axiosPrivate
                .get(
                    `${CaseGroupApi}?organisation_id=${userOrganisationId}`
                )
                .then((res) => {
                    setCaseGroupDropDown(res.data);
                });
        } catch (err: any) {
            if (err.response && err.response.status === 404) {
                toast.error(err.response.data.detail);
            } else {
                toast.error("Whoops! Something went wrong");
            }
        }
    }

    async function getCaseType() {
        try {
            axiosPrivate
                .get(
                    `${CaseTypeApi}?organisation_id=${userOrganisationId}&case_group_id=${CaseGroupDropDownValue}`
                )
                .then((res) => {
                    setCaseTypeDropDown(res.data.items);
                });
        } catch (err: any) {
            if (err.response && err.response.status === 404) {
                toast.error(err.response.data.detail);
            } else {
                toast.error("Whoops! Something went wrong");
            }
        }
    }

    const customerDropdown = {
        name: "contact_id",
        id: "contact_number, company_name, last_name, first_name",
        api: `${CustomerApi}`,
        label: `${Contact}`,
        type: "input-select",
        value: "",
        colSize: 6,
        required: true,
    };

    async function getLink() {
        try {
            const endPoint = `${CustomerLinkApi}?organisation_id=${userOrganisationId}&contact_id=${CustomerDropDownValue}`
            await linkDropdownField.fetchOptions(CustomerDropDownValue, false, endPoint);
            setLinkDropDown(linkDropdownField.options);
            /* axiosPrivate
                .get(
                    `${CustomerLinkApi}?organisation_id=${userOrganisationId}&contact_id=${CustomerDropDownValue}`
                )
                .then((res) => {
                    setLinkDropDown(res.data);
                }); */
        } catch (err: any) {
            if (err.response && err.response.status === 404) {
                toast.error(err.response.data.detail);
            } else {
                toast.error("Whoops! Something went wrong");
            }
        }
    }

    useEffect(() => {
        if (CaseGroupDropDownValue !== "0") getCaseType();
    }, [CaseGroupDropDownValue]);

    useEffect(() => {
        if (CustomerDropDownValue !== "0") getLink();
    }, [CustomerDropDownValue]);

    useLayoutEffect(() => {
        getCaseGroup();
        getPriority();
        getResolutionStatus();
    }, []);

    const getCaseURL = (data = {}) => {
        const filteredData = Object.fromEntries(
            Object.entries(data).filter(([_, value]) => value !== '' && value !== null && value !== undefined && value !== 0)
        );
    
        const params = new URLSearchParams({
            organisation_id: userOrganisationId,
            ...filteredData
        }).toString();
    
        return `${CaseApi}/report/full?${params}`;
    }

    const getEmployeeContactDetails = (contactDetails: IEmployeeBasicReportRes | null) => {
        if (!contactDetails) {
            return "";
        }

        const contactName = `${contactDetails?.last_name} ${contactDetails?.first_name} ${contactDetails?.middle_name}`;
      
        return `${contactName} (${contactDetails?.employee_number})`;
    };

    const getContactDetails = (contactDetails: IContactBasicRes) => {
        if (!contactDetails) {
            return "";
        }

        const contactType = contactDetails?.contact_type;
        const contactName =
          contactType === ContactTypeVal.Individual
            ? `${contactDetails?.last_name} ${contactDetails?.first_name} ${contactDetails?.middle_name}`
            : `${contactDetails?.company_name}`;
      
        return `${contactName} (${contactDetails?.contact_number})`;
    };
      
    const checkSLA = (
        dueDateStr: string | null | undefined, 
        resolvedDateStr: string | null | undefined
    ): string => {
        if (!dueDateStr) {
          return "Due date not specified";
        }
      
        const dueDate = new Date(dueDateStr);
        const resolvedDate = resolvedDateStr ? new Date(resolvedDateStr) : null;
        const currentDate = new Date();
      
        if (resolvedDate) {
          if (resolvedDate <= dueDate) {
            return "Met SLA"; 
          } else {
            return "SLA Not Met";  
          }
        } else {
          if (currentDate > dueDate) {
            return "SLA Not Met";  
          } else {
            return "Not Yet Due";  
          }
        }
    };

    async function getTableFunction() {
        const data = {organisation_id: userOrganisationId,
            case_group_id: CaseGroupDropDownValue,
            case_type_id: CaseTypeDropDownValue,
            priority_id: PriorityDropDownValue,
            resolution_status_id: ResolutionStatusDropDownValue,
            contact_id: CustomerDropDownValue,
            link_id: LinkDropDownValue,
            track_number:TrackingNumberValue,
            report:true,
            start_date: startDate ? formatDateWithTimeZone(startDate, "", "YYYY-MM-DD") : '', 
            end_date: endDate? formatDateWithTimeZone(endDate, "", "YYYY-MM-DD") : ''
        }
        setLoading(true); 
        setPrintedData([]);   
        await axiosPrivate
            .get(
                getCaseURL(data)
            )
            .then((res) => {
                const result: IClassReportRes[] = res.data;
                //const result: IClassReportRes[] = res.data.items;
                setPrintedData(result);
                setLoading(false);
            })
            .catch((err) => {
                setPrintedData([]);
                if (err.response && err.response.status === 404) {
                    toast.error(err.response.data.detail);
                } else {
                    toast.error("Whoops! Something went wrong");
                }
            })
            .finally(() => {
                setLoading(false);
            });;
    }

    function generatePrint(res: IClassReportRes[]) {
        const htmlContent = `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <style>
          table {
            font-family: arial, sans-serif;
            border-collapse: collapse;
            width: 100%;
          }

          td,
          th {
            border: 1px solid #dddddd;
            text-align: left;
            padding: 8px;
          }

          tr:nth-child(even) {
            background-color: #dddddd;
          }
        </style>
      </head>
      <body>
        <table>
          <thead>
             <tr>
              <th className="text-center">S/N</th>
              <th>Track Number</th>
              <th>Contact</th>
              <th>Contact Type</th>
              <th>Case</th>
              <th>Case Group</th>
              <th>Case Type</th>
              <th>Impact</th>
              <th>Urgency</th>
              <th>Priority</th>
              <th>Resolution Status</th>
              <th>Opened By</th>
              <th>Date Created</th>
              <th>Due Date</th>
              <th>SLA Status</th>
              <th>Resolved By</th>
              <th>Resolved By (Team)</th>
              <th>Resolved Date</th>
            </tr>
          </thead>
          <tbody>
            ${res
                .map(
                    (item, index) =>
                        `<tr>
                        <td className="text-center">${index + 1}</td>
                        <td>${item.track_number}</td>
                        <td>${item.contact_name}</td>
                        <td>${item.contact_type}</td>
                        <td>${item.subject}</td>
                        <td>${item.case_group}</td>
                        <td>${item.case_type}</td>
                        <td>${item.impact}</td>
                        <td>${item.urgency}</td>
                        <td>${item.priority}</td>
                        <td>${item.resolution_status}</td>
                        <td>${getEmployeeContactDetails(item.opened_by)}</td>
                        <td>${formatDateWithTimeZone(item.created_at)}</td>
                        <td>${formatDateWithTimeZone(item.due_date)}</td>
                        <td>${checkSLA(item.due_date, item.resolved_at)}</td>
                        <td>${getEmployeeContactDetails(item.resolved_by)}</td>
                        <td>${item.resolved_at
                        ? item.delegate_service_team !== null
                          ? item.delegate_service_team
                          : item.service_team
                        : ""}</td>
                        <td>${item.resolved_at ? formatDateWithTimeZone(item.resolved_at) : ""}</td>
                    </tr>`
                )
                .join("")}
            </tbody>
          <tfoot>
            <tr>
                <th className="text-center">S/N</th>
                <th>Track Number</th>
                <th>Contact</th>
                <th>Contact Type</th>
                <th>Case</th>
                <th>Case Group</th>
                <th>Case Type</th>
                <th>Impact</th>
                <th>Urgency</th>
                <th>Priority</th>
                <th>Resolution Status</th>
                <th>Opened By</th>
                <th>Date Created</th>
                <th>Due Date</th>
                <th>SLA Status</th>
                <th>Resolved By</th>
                <th>Resolved By (Team)</th>
                <th>Resolved Date</th>
            </tr>
          </tfoot>
        </table>

        <script>
        window.print();
        setTimeout(() => {
          window.close();
        if (!window.closed) {
          alert('Please allow popups for this website to close the window automatically.');
         }
        }, 100);
        </script>
      </body>
    </html>`;

        const blob = new Blob([htmlContent], { type: "text/html" });
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");
        URL.revokeObjectURL(blobUrl);
    }

    function exportToCSV(res: any[]) {
        const csvHeaders = [
            "S/N",
            "Track Number",
            "Contact",
            "Contact Type",
            "Link",
            "Case",
            "Case Group",
            "Case Type",
            "Impact",
            "Urgency",
            "Priority",
            "Resolution Status",
            "Opened By",
            "Date Created",
            "Due Date",
            "SLA Status",
            "Resolved By",
            "Resolved By (Team)",
            "Resolved Date",
        ];
        
        const data = res.map((item, index) => ({
            sn: index + 1,
            track_number: `"${item.track_number}"`,
            contact: item.contact_name,
            contact_type: item.contact_type,
            link: `"${item.link}"`,
            case: `"${item.subject}"`,
            case_group: item.case_group,
            case_type: item.case_type,
            impact: item.impact,
            urgency: item.urgency,
            priority: item.priority,
            resolution_status: item.resolution_status,
            opened_by: getEmployeeContactDetails(item.opened_by),
            date_created: `"${formatDateWithTimeZone(item.created_at)}"`,
            due_date: `"${formatDateWithTimeZone(item.due_date)}"`,
            sla_status: `"${checkSLA(item.due_date, item.resolved_at)}"`,
            resolved_by: getEmployeeContactDetails(item.resolved_by),
            resolved_by_team: `"${item.resolved_at
                        ? item.delegate_service_team !== null
                          ? item.delegate_service_team
                          : item.service_team
                        : ""}"`,
            resolved_at: `"${item.resolved_at ? formatDateWithTimeZone(item.resolved_at) : ""}"`
        }));

        const dataKeys: (keyof (typeof data)[0])[] = [
            "sn",
            "track_number",
            "contact",
            "contact_type",
            "link",
            "case",
            "case_group",
            "case_type",
            "impact",
            "urgency",
            "priority",
            "resolution_status",
            "opened_by",
            "date_created",
            "due_date",
            "sla_status",
            "resolved_by",
            "resolved_by_team",
            "resolved_at",
        ];

        const csvContent =
            csvHeaders.join(",") +
            "\n" +
            data
                .map((row) => `${dataKeys.map((key) => row[key]) + ","}`)
                .join("\n");

        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = URL.createObjectURL(blob);

        const downloadLink = document.createElement("a");
        downloadLink.href = url;
        downloadLink.download = `case_report_${formatDateWithTimeZone(new Date(), "", "YYYY_MM_DD_HH_MM_SS")}.csv`;

        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    }

    return {
        printedData,
        CaseGroupDropDown,
        CaseTypeDropDown,
        PriorityDropDown,
        ResolutionStatusDropDown,
        customerDropdown,
        LinkDropDown,
        dateRange,
        startDate,
        endDate,
        loading,
        setDateRange,
        exportToCSV,
        generatePrint,
        getTableFunction,
        getContactDetails,
        getEmployeeContactDetails,
        checkSLA,
        setCaseGroupDropDownValue,
        setCaseTypeDropDownValue,
        setPriorityDropDownValue,
        setResolutionStatusDropDownValue,
        setCustomerDropDownValue,
        setLinkDropDownValue,
        setLinkDropDown,
        setTrackingNumberValue,
    };
}
